"use client";

import React from "react";
import { BlockWrapper, H2, MarkdownRenderer, SectionTag } from "@/shared/components";
import { LazyGallery } from "@/shared/components/LazyGallery";
import type { ImageGalleryLayout } from "@/shared/components/imageGallery/ImageGallery";
import type { BlockSectionBg } from "./common/types";
import {
  getBodyTextClass,
  getMarkdownTextOverrideClass,
  getSectionBorderClass,
  getSectionGridCellClass,
  getSectionGridDesktopColumnsClass,
  getSectionGridShellClass,
  getSectionHeaderMobilePaddingClass,
  getSectionTaglineSidebarClass,
  sectionBgToWrapper,
} from "./blockSection";
import { resolveGalleryTheme } from "./galleryTheme";
import { GalleryItemInterface } from "@/lib/directus";
import { ImageDataType } from "@/shared/types";
import { bodyProseClass } from "@/shared/styles/typography";
import { cn } from "@/lib/utils";

interface GalleryBlockProps {
  headline?: string;
  tagline?: string;
  description?: string;
  items?: GalleryItemInterface[];
  layout?: ImageGalleryLayout;
  columnsPerRow?: 2 | 3 | 4;
  sectionBg?: BlockSectionBg;
}

export const GalleryBlock: React.FC<GalleryBlockProps> = ({
  headline,
  tagline,
  description,
  items = [],
  layout = "carousel",
  columnsPerRow = 3,
  sectionBg = "light",
}) => {
  const images: ImageDataType[] = items
    .map((item) => item.image)
    .filter((image): image is ImageDataType => image !== null);

  const theme = resolveGalleryTheme(sectionBg);
  const borderClass = getSectionBorderClass(sectionBg);
  const hasSidebar = Boolean(tagline);
  const hasHeader = Boolean(headline || tagline || description);
  const descriptionClass = cn(
    bodyProseClass,
    getBodyTextClass(sectionBg),
    theme.bodyText,
    getMarkdownTextOverrideClass(sectionBg)
  );

  const controlsTheme = {
    navButton: theme.navButton,
    paginationDotActive: theme.paginationDotActive,
    paginationDotInactive: theme.paginationDotInactive,
  };

  const gridShellClass = cn(
    getSectionGridShellClass(sectionBg),
    "grid-cols-2",
    getSectionGridDesktopColumnsClass(columnsPerRow)
  );
  const gridCellClass = getSectionGridCellClass(sectionBg);

  return (
    <BlockWrapper bgColor={sectionBgToWrapper(sectionBg)} noPadding>
      <div className="w-full min-w-0">
        {hasHeader && (
          <div
            className={cn(
              getSectionHeaderMobilePaddingClass(),
              "flex flex-col gap-4 border-b",
              "desktop:flex-row desktop:gap-0 desktop:border-b desktop:p-0",
              borderClass
            )}
          >
            {tagline && (
              <div
                className={cn(
                  getSectionTaglineSidebarClass(),
                  "desktop:w-1/3 desktop:border-r",
                  borderClass
                )}
              >
                <SectionTag
                  label={tagline}
                  variant={theme.tagVariant}
                  className="uppercase"
                />
              </div>
            )}

            <div
              className={cn(
                "flex flex-col gap-4",
                hasSidebar
                  ? "desktop:flex-1 desktop:px-10 desktop:py-20"
                  : "desktop:px-10 desktop:py-20"
              )}
            >
              {headline && (
                <H2
                  className={cn(
                    "text-heading-2-sm md:text-heading-2",
                    theme.headline
                  )}
                >
                  {headline}
                </H2>
              )}
              {description && (
                <MarkdownRenderer
                  content={description}
                  className={descriptionClass}
                />
              )}
            </div>
          </div>
        )}

        {images.length > 0 && (
          <div
            className={cn(
              "w-full min-w-0",
              layout === "carousel" && "overflow-hidden"
            )}
          >
            <LazyGallery
              images={images}
              layout={layout}
              columnsPerRow={columnsPerRow}
              controlsTheme={controlsTheme}
              gridShellClass={layout === "grid" ? gridShellClass : undefined}
              gridCellClass={layout === "grid" ? gridCellClass : undefined}
              className="w-full max-w-full"
            />
          </div>
        )}
      </div>
    </BlockWrapper>
  );
};
